import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

/**
 * PageMeta — small context where detail pages can publish their per-language
 * slug map so the LanguageSwitcher can build the equivalent URL in the target
 * language (e.g. /guides/luxury-car-rental-benidorm ↔ /es/guias/alquiler-coches-lujo-benidorm).
 *
 * Vehicles share a universal slug, but guides and routes have a `slug` shaped
 * as { en, es, ru }. Pages call `setSlugMap(slugMap)` after fetching and clear
 * it on unmount.
 */
const PageMetaContext = createContext({
  slugMap: null,
  setSlugMap: () => {},
});

export const PageMetaProvider = ({ children }) => {
  const [slugMap, setSlugMap] = useState(null);
  return (
    <PageMetaContext.Provider value={{ slugMap, setSlugMap }}>
      {children}
    </PageMetaContext.Provider>
  );
};

export const usePageMeta = () => useContext(PageMetaContext);

/** Hook for detail pages: publishes the slug map for the current page and
 *  clears it on unmount/key change. */
export function usePublishSlugMap(slugMap) {
  const { setSlugMap } = useContext(PageMetaContext);
  const stable = slugMap && typeof slugMap === "object" ? JSON.stringify(slugMap) : null;
  const apply = useCallback(() => {
    setSlugMap(slugMap || null);
  // `stable` is intentionally the sole dependency: it hashes the slugMap
  // contents so we don't re-fire on every parent render, only when the
  // logical value actually changes.
  }, [stable]);
  useEffect(() => {
    apply();
    return () => setSlugMap(null);
  }, [apply, setSlugMap]);
}
